import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tag } from 'antd';
import {
  ApartmentOutlined,
  ArrowsAltOutlined,
  ShrinkOutlined,
  FlagOutlined,
  RightOutlined,
  EyeOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { getEventIcon } from '../eventIcons';
import ScenarioBar from './ScenarioBar';
// STATUS_COLOR / STATUS_ICON_COLOR are no longer imported here: after the
// 2026-08-14 design update nothing on this surface is coloured by outcome. They are
// still exported for the detail panel's status pill.
import {
  SCORE_BAND,
  SCORE_BAND_COLOR,
  DEFAULT_SCORE_BANDS,
  scoreBand
} from '../../statusColors';
import { toLegend } from './toBands';
import style from './index.scss';

// The node circle no longer takes a hue from `event.status` — see the SCORE BANDS
// note in statusColors.js. The only coloured thing on a node is its score badge,
// and its hue is computed from the score, so the band becomes a modifier class
// (SCSS modules cannot read runtime values).
const BADGE_BAND_CLASS = {
  [SCORE_BAND.HIGH]: style.badgeHigh,
  [SCORE_BAND.MEDIUM]: style.badgeMedium,
  [SCORE_BAND.LOW]: style.badgeLow
};

// Legend for the score bands. It labels the SCORE BADGES — the one place a hue
// still appears on a node — and it is the key an analyst reads every score
// against, so it has to state the bands actually being applied.
//
// BUILT FROM CONFIGURATION (FIN-13569), not hardcoded. It read
// "High (70+) / Medium (40–69) / Low (<40)": names that do not exist in a
// configured tenant ("Medium" is not one of the three codes) and cut-offs that
// correspond to nothing. Both now come from `toBands.js`; this constant is only
// the fallback for a deployment with no configuration at all.
const DEFAULT_LEGEND = toLegend(undefined, DEFAULT_SCORE_BANDS);

// Sub-event list for a grouped node ("5 EVENTS" in the design). Rendered as a
// sibling of the node button, hanging beneath it.
//
// NEUTRAL, band-coloured (updated design, 2026-08-14). Previously the row tint and
// the icon wash came from the GROUP's status hue (a failed group washed all five
// rows red @3% with red icons @12%/40%), and the score text and bar fill came from
// each sub-event's OWN status. The updated design drops both: rows and icons read
// the same grey as everything else, and the only colour left is the bar, which
// follows the SCORE BAND — its "5 EVENTS" panel shows amber bars on scores 45/55/65
// (all Medium) inside a group whose own status is failed.
/**
 * The selection key for a CHILD row. A group keeps its first member's id (so its
 * node key and selection stay stable), which means a bare child id would collide
 * with the group itself and clicking the first child would toggle the group.
 */
export const childSelectionKey = (id) => `child:${id}`;

const SubEvents = ({ items, bands, selectedId, onSelectChild }) => {
  /* FIN-13581 — ASCENDING, WITH THE TRIGGER PINNED (product, 2026-09-24).
     The list stays in the §1 timestamp order, but the event that raised the alert
     is lifted out and held above the scrolling rows: on a 309-event group it was
     row 309 of 309, reachable only by scrolling past everything else. */
  const pinned = items.filter((c) => c.triggered);
  const rest = items.filter((c) => !c.triggered);

  const renderRow = (c) => {
    // Each sub-event's OWN glyph. This used to be a hardcoded SafetyOutlined,
    // so a group of password resets rendered five shields.
    const SubIcon = getEventIcon(c.icon);
    /* BANDS ON THE UNROUNDED SCORE. `c.score` is the rounded value the row
           DISPLAYS; banding from it disagrees with the server near a cut-off
           (66.4 rounds to 66 and bands Good here, High there). */
    const band = scoreBand(c.rawScore ?? c.score, bands);
    /* CLICKABLE: a child row opens ITS OWN event in the side panel — the
           group's panel speaks for all members and cannot show one member's
           fields. A button, so it is reachable by keyboard like the nodes. */
    const selected = selectedId === childSelectionKey(c.id);
    return (
      <button
        type="button"
        className={`${style.subRow}${
          selected ? ` ${style.subRowSelected}` : ''
        }`}
        key={c.id}
        aria-pressed={selected}
        onClick={() => onSelectChild && onSelectChild(c)}>
        <span className={style.subIcon}>
          <SubIcon />
        </span>
        <span className={style.subMain}>
          {/* Design puts time and score on ONE row (`flex items-center
                justify-between gap-1`) above the label — not the score in a
                separate right-hand column. */}
          <span className={style.subMeta}>
            <span className={style.subTime}>{c.time}</span>
            {/* The score READS dark in the updated design, not tinted — the
                    colour is carried by the bar underneath it. Colour comes from
                    SCSS now; only the band-dependent bar stays inline. */}
            {c.score != null ? (
              <span className={style.subScore}>{c.score}</span>
            ) : null}
          </span>
          {/* PRD §7: "Expanding the group identifies the exact triggering
                  child event." The collapsed group already carries the pill; this
                  says WHICH occurrence inside it. */}
          {/* `title` for the same reason .nodeLabel carries one: the row is
                  170px wide and clips with an ellipsis, so the only way to read
                  the full text is a hover. `fullLabel` is the BE's untruncated
                  string — `label` is already capped by truncateLabel, so falling
                  back to it would show the same clipped text the tooltip is
                  meant to reveal. */}
          <span className={style.subLabel} title={c.fullLabel || c.label}>
            {c.label}
            {c.triggered ? (
              <span className={style.subTrigger}> TRIGGER</span>
            ) : null}
          </span>
          {/* NO PER-CHILD DETAIL LINE.
                  This row is 170px wide and already holds a time, a label, a
                  TRIGGER pill and a score bar. It used to show the child's
                  amount, answering "collapsing transactions hides the money" —
                  but FIN-12628 removed `detail.amount` and there is no
                  successor the mapper can identify, so nothing goes here.
                  Rendering the child's configured fields instead was tried and
                  reverted (2026-09-01): three `label: value` pairs wrap over six
                  lines and turn the sub-list into a wall of text. Those details
                  belong in the detail panel, which already shows them.
                  Restoring a per-child amount needs BE to mark which field is
                  money — raised alongside the group total. */}
          {c.score != null ? (
            <span className={style.subBarTrack}>
              {/* Width AND hue both encode the score, so both are inline —
                      CSS cannot compute either from a number. */}
              <span
                className={style.subBarFill}
                style={{
                  width: `${c.score}%`,
                  background: SCORE_BAND_COLOR[band]
                }}
              />
            </span>
          ) : null}
        </span>
      </button>
    );
  };

  return (
    <div className={style.subEventsPanel}>
      <div className={style.subHead}>{items.length} events</div>
      {pinned.length ? (
        <div className={style.subPinned}>{pinned.map(renderRow)}</div>
      ) : null}
      <div className={style.subList}>{rest.map(renderRow)}</div>
    </div>
  );
};

SubEvents.propTypes = {
  items: PropTypes.array.isRequired,
  bands: PropTypes.object,
  selectedId: PropTypes.string,
  onSelectChild: PropTypes.func
};

const EventNode = ({ event, selected, expanded, onSelect, bands }) => {
  const Icon = getEventIcon(event.icon);
  // PRD §7 specifies "ACT xN or TXN xN" — the multiplier applies to whichever
  // kind the group is, not to activities only. Previously hardcoded to ACT, so a
  // grouped transaction would have rendered a bare TXN with no count.
  // A single (ungrouped) event carries no multiplier; it reads "x1" so every
  // node is labelled the same way (FIN-13733).
  // No type -> no tag, as before; never "undefined x1".
  const typeTagLabel = event.type
    ? `${event.type} x${event.multiplier ?? 1}`
    : undefined;

  // Band, not status: the circle is neutral and only the badge is coloured.
  // Banded on the unrounded score — see the note in SubEvents.
  const band = scoreBand(event.rawScore ?? event.score, bands);

  return (
    <button
      type="button"
      className={[
        style.eventNode,
        selected ? style.selected : '',
        event.triggered ? style.triggered : ''
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onSelect(event.id)}>
      {/* Trigger halo. Its own element, as in the design, so it can pulse without
          animating the circle — and rendered only when triggered, so a plain node
          has no stray absolute box in its layout. aria-hidden: the TRIGGER tag
          below already states this in text, so the halo is pure decoration. */}
      {event.triggered ? (
        <div
          className={`${style.triggerHalo}${
            expanded ? ` ${style.triggerHaloLg}` : ''
          }`}
          aria-hidden="true"
        />
      ) : null}
      {/* 32px collapsed, 40px expanded — the design grows the circle with the
          detail lines rather than keeping one size. */}
      <div
        className={`${style.nodeCircle}${
          expanded ? ` ${style.nodeCircleLg}` : ''
        }`}>
        <Icon />
        {/* Only a SCORED event gets a badge. This used to render unconditionally,
            which put an empty coloured disc on every unscored node; the updated
            design's "Login Attempt" node carries no badge at all. */}
        {band ? (
          <span className={`${style.scoreBadge} ${BADGE_BAND_CLASS[band]}`}>
            {event.score}
          </span>
        ) : null}
        {event.multiplier ? (
          <span className={style.multiplierBadge}>{event.multiplier}</span>
        ) : null}
      </div>
      {/* title carries the untruncated text, so a capped label is still
          readable on hover without opening the panel. */}
      <span className={style.nodeLabel} title={event.fullLabel || event.label}>
        {event.label}
      </span>
      <span className={style.nodeTime}>{event.time}</span>
      {/* Summary FIRST, then the facts (value only since FIN-13566) — the
          design's expanded node reads "IP: 103.45.67.89 · Bangkok" and then
          "Device: …", "IP Address: …",
          "Activity ID: …". They are not alternatives; the summary is the
          one-line story and the lines are the supporting fields. */}
      {/* ONLY the summary is gated on `expanded`. The fact lines were gated too
          until the design's COLLAPSED screenshots were checked (2026-08-18): they
          already carry Device / IP Address / Activity ID on every node, so the
          toggle adds the one-line story on top rather than revealing the fields.
          Keeping them always-on also stops the track height jumping by three rows
          on every toggle. */}
      {expanded && event.summary ? (
        <span className={style.nodeSummary}>{event.summary}</span>
      ) : null}
      {event.nodeLines?.length
        ? event.nodeLines.map((l) => (
            <span
              key={l.label}
              className={style.nodeLine}
              /* `l.title` when the line is a SUMMARY — a grouped identifier
                 reads "first +N more", so the hover has to carry the full set
                 rather than repeat the abbreviation. */
              title={`${l.label}: ${l.title || l.value}`}>
              {/* VALUE ONLY ON THE TILE (FIN-13566, product decision 2026-09-28).
                  ~17 characters fit, so "Transaction Number: " left room for
                  "QA_TXN_". The label is in the hover title for the mouse and in
                  a visually hidden span for screen readers, which never see a
                  child's title. */}
              <span className={style.visuallyHidden}>{l.label}: </span>
              {l.value}
            </span>
          ))
        : null}
      <div className={style.nodeTags}>
        {/* No antd `color="red"` preset — it ships a pink fill and a different
            red. The design's amount pill is outline-only and monospace. */}
        {event.amount ? (
          <Tag className={style.amountTag}>{event.amount}</Tag>
        ) : null}
        {/* No antd `color="blue"` preset — it is a different blue family from the
            design's --primary. The pill is styled from the token in SCSS. */}
        {event.triggered ? (
          <Tag icon={<FlagOutlined />} className={style.triggerTag}>
            TRIGGER
          </Tag>
        ) : null}
        {typeTagLabel ? (
          <Tag className={style.typeTag}>{typeTagLabel}</Tag>
        ) : null}
      </div>
    </button>
  );
};

EventNode.propTypes = {
  event: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  // Timeline-level expanded state; reveals `event.summary` under the timestamp.
  expanded: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  bands: PropTypes.object
};

// Captions from the design. Constants rather than BE fields: they describe how
// the view works, not the data.
//
// TWO of them, because the row means something different with and without a
// scenario selected — but both are now `contextMode=triggered`, so neither claims
// to be the account's history.
// COPY NEEDS PRODUCT SIGN-OFF: the second sentence is drafted here, not designed.
const SCENARIO_NOTE =
  'This sequence represents conditions that triggered the alert, not a ' +
  "single account's history. Events may span multiple entities.";

const ALL_EVENTS_NOTE =
  'Showing every event that contributed to this alert. The event tagged ' +
  'TRIGGER is the one that raised it — select a scenario to narrow the row.';

// Mirror the stylesheet: $connector-net-w, $track-pad-x, $visible-nodes.
// Duplicated because they are needed as NUMBERS here; change both together.
const TRACK_GAP = 12;
const TRACK_PAD_X = 0; // the nodes carry their own 8px; see $track-pad-x
const VISIBLE_NODES = 10;

const EventTimeline = ({
  sequence,
  selectedId,
  onSelect,
  isLoading,
  error,
  selectedScenarioIndex,
  onScenarioSelect,
  // FIN-13570 — optional. Supplied by the shell so maximising can also close
  // the event panel; omitted elsewhere, and the toggle then manages itself.
  expanded: expandedProp,
  onExpandedChange,
  // FIN-13569 — the tenant's configured legend and its cut-offs. Both optional:
  // the fixture path and any caller without a store render the shipped defaults
  // rather than an empty key.
  legend,
  bands
}) => {
  const allEvents = sequence?.events || [];
  const scenarios = sequence?.scenarios;

  // SCENARIOS. An alert can fire for several reasons and the design pages between
  // them, showing one scenario's events at a time. Index rather than id so the
  // dropdown and the ‹ › pager drive one value.
  //
  // CONTROLLED IF PROVIDED. Live scenarios come from a separate endpoint and
  // selecting one REFETCHES the timeline with ?typologyId=, so the container owns
  // the value; it passes both props and this component just renders the choice.
  // The mock fixture filters client-side with no fetch, so it gets neither prop
  // and the internal state below still drives it — one component, both paths.
  const [internalScenarioIndex, setInternalScenarioIndex] = useState(0);
  const isScenarioControlled =
    typeof selectedScenarioIndex === 'number' &&
    typeof onScenarioSelect === 'function';
  const scenarioIndex = isScenarioControlled
    ? selectedScenarioIndex
    : internalScenarioIndex;
  const setScenarioIndex = isScenarioControlled
    ? onScenarioSelect
    : setInternalScenarioIndex;

  // Guarded against a shrinking list: if `scenarios` is refetched shorter while a
  // later one is selected, an unclamped index would render an empty timeline that
  // looks like "this scenario has no events".
  const activeScenario = scenarios?.length
    ? scenarios[Math.min(scenarioIndex, scenarios.length - 1)]
    : undefined;

  // Scenarios reference events by id because they OVERLAP — the design's
  // "Re-Login" and "Fund Transfer" belong to both. Filtering the canonical list
  // preserves chronological order for free, and an event named by two scenarios
  // is still ONE object with ONE id, so selection keeps working across a switch.
  //
  // `eventIds` is the SIGNAL, not just the data: the mock fixture carries it and
  // is filtered here, while the live BE never sends it — there, the server has
  // already filtered the payload via ?typologyId= and re-filtering against an
  // absent list would empty the timeline. Absent eventIds therefore means "render
  // what you were given", which is also what the "All events" entry needs.
  const events = useMemo(() => {
    if (!activeScenario || !Array.isArray(activeScenario.eventIds)) {
      return allEvents;
    }
    const wanted = new Set(activeScenario.eventIds);
    return allEvents.filter((e) => wanted.has(e.id));
  }, [allEvents, activeScenario]);

  // Switching scenario must not leave the side panel describing a node that is no
  // longer on screen — the panel would be the only evidence of an event the
  // timeline is not showing, which reads as a rendering bug.
  useEffect(() => {
    if (!activeScenario || !selectedId) return;
    const onScreen = events.some(
      (e) =>
        e.id === selectedId ||
        (e.children || []).some((c) => childSelectionKey(c.id) === selectedId)
    );
    if (!onScreen) onSelect(null, null);
  }, [activeScenario, selectedId, events]);

  // A grouped node's sub-event list is its OWN toggle, independent of selection.
  // Verified against the design: expand "Failed OTP Attempts", then select
  // "Password Reset" — the list STAYS OPEN, and only clicking the grouped node
  // again closes it.
  // This was briefly derived from `selectedId` instead, on the assumption that a
  // list left open while another node was selected was a desync bug. It isn't —
  // it's the specified behaviour, and deriving it made the list close whenever
  // any other node was clicked.
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  const handleSelect = (id) => {
    const target = events.find((e) => e.id === id);
    // The events live here (fetched by the container), so the selected event is
    // handed UP rather than looked up again by the shell — which only has the
    // dev fixture and would find nothing once live data is in play.
    // Only grouped nodes have something to toggle; clicking a plain node must
    // leave an already-open list alone.
    // `opening` is captured before the state update because it decides the
    // selection below.
    const isGroup = Boolean(target?.children?.length);
    const opening = isGroup && expandedGroupId !== id;
    if (isGroup) {
      setExpandedGroupId((prev) => (prev === id ? null : id));
    }
    // Clicking the ALREADY-SELECTED node clears the selection, so a second click
    // removes the blue ring and closes the side panel — the same end state as the
    // panel's ✕. The ring and the panel are one piece of state, so they must
    // always agree; leaving the ring on with no panel (or vice versa) would tell
    // the analyst a node is "current" when nothing is being described.
    // NOTE this is deliberately NOT the grouped-node toggle above: expansion is
    // its own state, so clicking a *different* node still leaves an open list
    // alone (see the comment on expandedGroupId).
    // EXCEPT when the click is OPENING a group's list. Expansion and selection
    // toggled in opposite phases, so re-clicking an already-selected group
    // opened the list while closing the panel — and the two could never be open
    // together. Reported 2026-09-01: "on click the right panel is not there …
    // on again click then right panel is coming."
    // Opening the list is an act of inspecting the node, so it selects.
    if (id === selectedId && !opening) {
      onSelect(null, null);
      return;
    }
    // Otherwise selection follows the click, so the panel tracks the node even
    // when the click is collapsing its list.
    onSelect(id, target || null);
  };

  // A child row of an open group: the side panel shows THAT event. Clicking the
  // selected child again clears it, matching a node's second click.
  const handleSelectChild = (child) => {
    const key = childSelectionKey(child.id);
    if (key === selectedId) {
      onSelect(null, null);
      return;
    }
    onSelect(key, child);
  };

  // Timeline expand/collapse (the ⤢ / ⤡ control in the header). Expanding
  // reveals each event's one-line summary; it is independent of node selection.
  // FIN-13570 — CONTROLLABLE FROM ABOVE.
  //
  // Maximising has to close the right-hand event panel, and that panel is a
  // SIBLING of this component (FramlAlertDetailShell's .spatialRow). State kept
  // here could only ever resize nodes inside this card — which is exactly what
  // the ticket reported as "nothing happens": the control worked, but it could
  // not reach the thing that needed to move.
  //
  // Stays uncontrolled when the props are absent, so every existing caller and
  // test keeps today's behaviour without being updated.
  const [ownExpanded, setOwnExpanded] = useState(false);
  const isControlled = typeof expandedProp === 'boolean';
  const detailsExpanded = isControlled ? expandedProp : ownExpanded;
  const setDetailsExpanded = (next) => {
    const value = typeof next === 'function' ? next(detailsExpanded) : next;
    if (!isControlled) setOwnExpanded(value);
    if (onExpandedChange) onExpandedChange(value);
  };
  const ToggleIcon = detailsExpanded ? ShrinkOutlined : ArrowsAltOutlined;

  // NO HALF-DRAWN NODE AT THE RIGHT EDGE.
  //
  // The stylesheet caps the track at a whole number of nodes
  // (10 * 112 + 9 * 12 + 2 * 8 = 1244px), but that is a MAXIMUM. Give the track
  // less — a narrower window, or the event panel open — and it renders at
  // whatever it gets, which is almost never a whole number of nodes. The row
  // then ends part-way through one and shows a sliver, which reads as a
  // rendering fault rather than as "there is more to scroll".
  //
  // This repo already made that call for the grouped-events sub-panel: the
  // "a clipped row advertises the scroll" rationale was REVERSED on 2026-08-23
  // in favour of whole rows (see the $sub-panel-max-h note in the stylesheet).
  // A part-node is the same thing sideways.
  //
  // Reads --node-w rather than assuming 112, so the expanded state (132px) goes
  // through the same arithmetic.
  const trackRef = useRef(null);
  useEffect(() => {
    const track = trackRef.current;
    const parent = track?.parentElement;
    if (!track || !parent || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const fit = () => {
      const nodeW = parseFloat(
        getComputedStyle(track).getPropertyValue('--node-w')
      );
      if (!nodeW) return;
      // A node plus the connector after it. The LAST node has no connector,
      // hence +GAP when solving for n and -GAP when rebuilding the width.
      const pitch = nodeW + TRACK_GAP;
      const usable = parent.clientWidth - TRACK_PAD_X * 2 + TRACK_GAP;
      const n = Math.max(
        1,
        Math.min(VISIBLE_NODES, Math.floor(usable / pitch))
      );
      track.style.maxWidth = `${n * pitch - TRACK_GAP + TRACK_PAD_X * 2}px`;
    };

    fit();
    // Observes the PARENT, never the track itself — resizing what you observe
    // is how a ResizeObserver loop starts.
    const observer = new ResizeObserver(fit);
    observer.observe(parent);
    return () => observer.disconnect();
    // Re-runs on expand: --node-w changes, so the arithmetic does too.
  }, [detailsExpanded]);

  // The BE caps `contextMode=full` server-side and flags it with `truncated`,
  // while `total` (its eventCount) keeps the TRUE number. Rendering `total`
  // beside a track built from the events actually sent claims a count we are not
  // showing — on a fraud timeline the missing events are precisely the ones an
  // analyst cannot know about. So say "Showing X of Y" and warn.
  // Guarded on `total > events.length`: a truncated flag with nothing actually
  // dropped is not worth a warning, and crying wolf trains analysts to ignore it.
  const shownCount = events.length;
  const trueTotal = sequence?.total;
  // With a scenario selected the badge counts THAT scenario's nodes (the design
  // reads "6 events" then "3 events" on the same alert), not the alert total —
  // `total` describes the whole alert and would contradict the row on screen.
  // Truncation is likewise an alert-level statement, so it is suppressed here:
  // "Showing 6 of 7" beside a deliberately filtered row would read as data loss.
  // Keyed on a scenario that actually NARROWS the list. "All events" is present in
  // the dropdown but shows everything, so the alert-level total and the truncation
  // warning both still apply under it.
  const isNarrowedByScenario = Boolean(
    activeScenario &&
      (Array.isArray(activeScenario.eventIds) ||
        activeScenario.variantName != null)
  );
  const isTruncated =
    !isNarrowedByScenario &&
    Boolean(sequence?.truncated) &&
    trueTotal != null &&
    trueTotal > shownCount;
  const countLabel = isNarrowedByScenario
    ? `${shownCount} events`
    : `${trueTotal ?? shownCount} events`;

  return (
    <div className={style.timelineCard}>
      <div className={style.timelineHeader}>
        <span className={style.title}>
          <ApartmentOutlined />
          Alert Details
          {/* Spacing comes from .title's `gap: 8px` (the design's gap-2) alone —
              see index.scss. No inline margin here. */}
          <Tag>
            {isTruncated
              ? `Showing ${shownCount} of ${trueTotal} events`
              : countLabel}
          </Tag>
        </span>
        <span className={style.legend}>
          {(legend || DEFAULT_LEGEND).map((l) => (
            <span key={l.key}>
              <span
                className={style.legendDot}
                style={{ background: l.color }}
              />
              {l.label}
            </span>
          ))}
          <button
            type="button"
            className={style.legendToggle}
            aria-expanded={detailsExpanded}
            aria-label={
              detailsExpanded
                ? 'Collapse event details'
                : 'Expand event details'
            }
            onClick={() => setDetailsExpanded((v) => !v)}>
            <ToggleIcon />
          </button>
        </span>
      </div>

      {/* Rendered only when the BE actually sent scenarios, so today's live data
          (which has none) keeps exactly the timeline it has now. */}
      {scenarios?.length ? (
        <ScenarioBar
          scenarios={scenarios}
          selectedIndex={Math.min(scenarioIndex, scenarios.length - 1)}
          onSelect={setScenarioIndex}
        />
      ) : null}

      {/* The scenario's own narrative — "Repeated OTP failures followed by a
          password reset…". Sits ABOVE the standing note, matching the design:
          the description is about THIS scenario, the note is about the timeline
          in general, so the specific precedes the general. */}
      {activeScenario?.description ? (
        <p className={style.scenarioDescription}>
          {activeScenario.description}
        </p>
      ) : null}

      {isTruncated ? (
        <p role="status" aria-live="polite" className={style.timelineTruncated}>
          <WarningOutlined />
          This timeline is incomplete — {trueTotal - shownCount} earlier{' '}
          {trueTotal - shownCount === 1 ? 'event was' : 'events were'} not
          returned.
        </p>
      ) : null}

      {/* The design's standing caption under the scenario bar. Shown only when a
          typology is actually selected: it asserts the row is "conditions that
          triggered the alert, not a single account's history", which is TRUE of a
          typology-filtered timeline and FALSE of "All events" — that request is
          contextMode=full, i.e. exactly the account history the sentence denies.
          `sequence.note` (the fixture's own copy of this text) wins when present,
          so the mock renders it once rather than twice. */}
      {sequence?.note || scenarios?.length ? (
        <p className={style.timelineNote}>
          {sequence?.note ||
            (isNarrowedByScenario ? SCENARIO_NOTE : ALL_EVENTS_NOTE)}
        </p>
      ) : null}

      {/* Three OUTCOMES, three different messages. "No event data available"
          is a claim about the CUSTOMER — that this party had no activity — so
          it may only be shown once the request has actually succeeded. While a
          request is in flight, or after it failed, that sentence would tell an
          analyst something false about the subject of a fraud investigation.
          The BE-1 endpoint really does fail this way: it 400s for any alert
          whose ALERT_SOURCE_ID is not Fraud Prevention.
          Error wins over loading — a failed refetch is not still in progress. */}
      {error ? (
        <div role="alert" className={style.timelineError}>
          <WarningOutlined />
          Could not load the event timeline. Try reloading the page.
        </div>
      ) : null}

      {!error && isLoading ? (
        <div role="status" aria-live="polite" className={style.timelineHint}>
          <EyeOutlined />
          Loading events…
        </div>
      ) : null}

      {!error && !isLoading && events.length === 0 ? (
        // The request succeeded and the party genuinely has no events — the one
        // case where this claim is true.
        <div className={style.timelineHint}>
          <EyeOutlined />
          No event data available for this alert
        </div>
      ) : null}

      {/* --node-w lives here: one switch drives the node box, the
          $visible-nodes viewport cap and the sub-event panel insets. */}
      <div
        ref={trackRef}
        className={`${style.timelineTrack}${
          detailsExpanded ? ` ${style.timelineTrackLg}` : ''
        }`}>
        <div className={style.timelineRow}>
          {events.map((event, idx) => (
            <React.Fragment key={event.id}>
              {idx > 0 ? (
                <div className={style.connector}>
                  <span className={style.dash} />
                  <RightOutlined className={style.arrow} />
                </div>
              ) : null}
              <div className={style.nodeSlot}>
                <EventNode
                  event={event}
                  selected={event.id === selectedId}
                  expanded={detailsExpanded}
                  onSelect={handleSelect}
                  bands={bands}
                />
                {expandedGroupId === event.id && event.children?.length ? (
                  <SubEvents
                    items={event.children}
                    bands={bands}
                    selectedId={selectedId}
                    onSelectChild={handleSelectChild}
                  />
                ) : null}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

EventTimeline.propTypes = {
  legend: PropTypes.array,
  bands: PropTypes.object,
  sequence: PropTypes.object,
  // BE-1 request state, supplied by the container. Both are needed to tell the
  // three outcomes apart — see the render comment above.
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  // Supplied together by the container for LIVE scenarios (selection triggers a
  // refetch). Omitted for the mock fixture, which filters client-side.
  selectedScenarioIndex: PropTypes.number,
  onScenarioSelect: PropTypes.func,
  // Controlled maximise. Boolean makes it controlled; undefined leaves the
  // component managing its own.
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func
};

export default EventTimeline;
